/**
 * Door kinds and door data (PRP-SKETCH-FIELD-3 H7, H10, H11, J9 to J12; data
 * contract DD-SF3.1): `door.type` is the J9 kind, plus `panels`, `bifoldSide`,
 * `cased` and `stained`. Every option list and default is the policy's
 * (`door_kind_options`, `door_panels_default`, `bifold_side_default`,
 * `door_cased_default`, `stained_default`); no kind name is written here.
 *
 * - `migrateDoors(plan)`: doors saved before FIELD-3 get the defaults on load.
 * - `doorWithDefaults(door)`: a door's fields made whole and consistent with its kind.
 * - `setDoorType`: the kind chips (panels and bifold side follow the kind).
 * - The stained prompt (J11) is in `doorsStained.ts`.
 */
import { POLICY } from './policy';
import { findRoom } from './geom';
import { adjacent, partnerOf, syncLinked } from './shared';
import type { BifoldSide, DoorPanels, DoorSide, DoorV2, Fail, Opening, Plan, Room } from './types';

/**
 * The data contract's "double" panel value (DD-SF3.1). The policy has no key that
 * names it (it lists it among each kind's choices), so it is named once here.
 */
export const PANELS_DOUBLE: DoorPanels = 'double'; // policy-literal-ok: data-contract enum value, not a default

/** A kind's panel choices; empty when the kind has none (swing doors). */
export const panelChoices = (type: string): DoorPanels[] => POLICY.door_kind_options[type]?.panels ?? [];
/** A kind's fold sides; empty unless the kind folds (Bifold). */
export const bifoldChoices = (type: string): BifoldSide[] => POLICY.door_kind_options[type]?.bifold_side ?? [];
/** A kind that is always a pair (French, J9): the policy's double-door list. */
export const alwaysDouble = (type: string): boolean => POLICY.esx_double_door.includes(type);

/** The panels a door of this kind starts with. */
export const defaultPanels = (type: string): DoorPanels => (alwaysDouble(type) ? PANELS_DOUBLE : POLICY.door_panels_default);

/** J10: casing defaults on for every kind; the policy may turn a kind off. */
export const casedDefault = (type: string): boolean => POLICY.door_cased_default[type] ?? true;

/** J11: the stained state a new door starts with. Interior doors follow the device default; exterior the policy. */
export function stainedDefault(plan: Plan, side: DoorSide): boolean {
  if (side === 'exterior') return POLICY.stained_default;
  return plan.defaults.doorStained ?? POLICY.stained_default;
}

/** The data contract's fold sides (DD-SF3.1), relative to the room holding the copy, like `out`. */
const SIDE_OUT: BifoldSide = 'out'; // policy-literal-ok: the in/out vocabulary (data contract), not a default
const SIDE_IN: BifoldSide = 'in'; // policy-literal-ok: the in/out vocabulary (data contract), not a default
/** B-7357: the fold side a swing draws (`out` = into the room across). */
export const sideOfSwing = (out: boolean): BifoldSide => (out ? SIDE_OUT : SIDE_IN);
/** Which fact wins when a bifold's side and swing are made one: the side (the Folds chip) or the swing (Swing in/out). */
export type BifoldBy = 'side' | 'swing';

/**
 * B-7357: on a folding kind, `bifoldSide` and `out` are one fact (the export reads the
 * side, the drawing the swing). `by: 'side'` sets the swing from the side; `by: 'swing'`
 * the side from the swing. A side the kind does not offer, or none, is read from the
 * swing (DD-SF3.10: a bifold with no side always exported its `out`). Mutates `d`.
 */
export function alignBifold(d: DoorV2, by: BifoldBy): DoorV2 {
  const sides = bifoldChoices(d.type);
  if (!sides.length) { delete d.bifoldSide; return d; }
  const valid = d.bifoldSide != null && sides.includes(d.bifoldSide);
  if (by === 'side' && valid) d.out = d.bifoldSide === SIDE_OUT;
  else {
    const side = sideOfSwing(!!d.out);
    d.bifoldSide = sides.includes(side) ? side : POLICY.bifold_side_default;
    d.out = d.bifoldSide === SIDE_OUT;
  }
  return d;
}

/**
 * A door's fields made whole and consistent with its kind: absent fields take the
 * migration defaults (panels per kind, cased per kind, stained per `stained_default`),
 * a kind that is always double is double, a panel choice the kind does not offer goes
 * back to the default, a bifold side on a kind that does not fold is dropped, and a
 * bifold's side and swing agree (`alignBifold`, `by` the side unless told otherwise).
 * Returns a new object.
 */
export function doorWithDefaults(d: DoorV2, by: BifoldBy = 'side'): DoorV2 {
  const out: DoorV2 = { ...d };
  const choices = panelChoices(d.type);
  if (alwaysDouble(d.type)) out.panels = PANELS_DOUBLE;
  else if (out.panels == null || (choices.length ? !choices.includes(out.panels) : out.panels !== POLICY.door_panels_default)) {
    out.panels = POLICY.door_panels_default;
  }
  alignBifold(out, by);
  if (out.cased == null) out.cased = casedDefault(d.type);
  if (out.stained == null) out.stained = POLICY.stained_default;
  return out;
}

/** The door fields of a new door of `type` on a wall of `side` (a new bifold folds `bifold_side_default`). */
export function newDoorFields(plan: Plan, type: string, side: DoorSide): DoorV2 {
  const fold = bifoldChoices(type).length ? { bifoldSide: POLICY.bifold_side_default } : {};
  return doorWithDefaults({
    type, out: false, hingeAtEnd: false, cased: casedDefault(type), stained: stainedDefault(plan, side), ...fold,
  });
}

export interface DoorMigration {
  /** Doors that got at least one field. */
  doors: { rid: string; oid: number }[];
}

/**
 * B-7357 on load: a bifold pair across a shared wall whose copies do not mirror (saved
 * before the fix) is settled the way the export reads it (`bifold_opens_into_own` in
 * app/services/placement/field_doors.py): the copy saying `in` owns the fold; if both
 * say the same side, the copy whose `out` is false; if that cannot say either, the copy
 * of the earlier room (the one the export writes) keeps its side. The other copy
 * mirrors. So migrating never changes what the ESX says.
 */
function settleBifoldPairs(plan: Plan): void {
  plan.rooms.forEach((r, ri) => r.openings.forEach((o) => {
    if (o.kind !== 'door' || !o.door || !bifoldChoices(o.door.type).length) return;
    const P = adjacent(plan, r, o);
    if (!P || !P.o.door || P.o.door.type !== o.door.type || plan.rooms.indexOf(P.r) < ri) return;
    const d = o.door, q = P.o.door;
    const mine = d.bifoldSide === SIDE_IN, theirs = q.bifoldSide === SIDE_IN;
    let own: boolean | null = null;
    if (mine !== theirs) own = mine;
    else if (!!d.out !== !!q.out) own = !d.out;
    const dOut = own != null ? !own : (d.bifoldSide != null ? d.bifoldSide === SIDE_OUT : !!d.out);
    d.bifoldSide = sideOfSwing(dOut);
    q.bifoldSide = sideOfSwing(!dOut);
    d.out = dOut;
    q.out = !dOut;
  }));
}

/**
 * Item 2.4: on load, every door saved before FIELD-3 gets the new fields at their
 * defaults. Its type, width, position, swing and hinge are unchanged, and a door
 * that already has the fields is not touched. Doors with no `door` object (they
 * read their type from the wall) are left as they are. A bifold's side and swing are
 * made one fact (B-7357): a pair settled as the export reads it, a lone copy by its
 * side (what it exported), a copy with no side by its swing.
 */
export function migrateDoors(plan: Plan): DoorMigration {
  const res: DoorMigration = { doors: [] };
  const before = new Map<Opening, string>();
  plan.rooms.forEach((r) => r.openings.forEach((o) => { if (o.door) before.set(o, JSON.stringify(o.door)); }));
  settleBifoldPairs(plan);
  for (const r of plan.rooms) {
    for (const o of r.openings) {
      if (o.kind !== 'door' || !o.door) continue;
      const next = doorWithDefaults(o.door);
      if (JSON.stringify(next) === before.get(o)) continue;
      o.door = next;
      res.doors.push({ rid: r.id, oid: o.id });
    }
  }
  return res;
}

function doorAt(plan: Plan, rid: string, oid: number): { r: Room; o: Opening } | null {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  return r && o && o.kind === 'door' ? { r, o } : null;
}

/**
 * The kind chips (J9): set a door's kind. Panels and bifold side follow the kind
 * (kept when the new kind offers them); casing and stained are kept. The linked
 * copy across a shared wall follows (F7). One undo step.
 */
export function setDoorType(plan: Plan, rid: string, oid: number, type: string): { ok: true } | Fail<'missing' | 'invalid'> {
  const d = doorAt(plan, rid, oid);
  if (!d) return { ok: false, err: 'missing', message: 'That door is gone' };
  if (!POLICY.door_types.includes(type)) return { ok: false, err: 'invalid', message: 'Pick a door kind' };
  const cur = d.o.door ?? newDoorFields(plan, type, 'interior');
  // B-7357: a door switched to a folding kind keeps the swing it shows (the side follows it).
  d.o.door = doorWithDefaults({ ...cur, type }, cur.type === type ? 'side' : 'swing');
  syncLinked(plan, d.r, d.o);
  syncUnlinked(plan, d.r, d.o);
  return { ok: true };
}

/**
 * A linked copy that is no longer across a shared wall still carries the same door data
 * (its own swing and hinge kept; a bifold's side follows its own swing, B-7357).
 */
export function syncUnlinked(plan: Plan, r: Room, o: Opening): void {
  const P = partnerOf(plan, r, o);
  if (P && o.door && P.o.kind === 'door') {
    P.o.door = alignBifold({ ...o.door, out: P.o.door?.out ?? false, hingeAtEnd: P.o.door?.hingeAtEnd ?? false }, 'swing');
  }
}
